import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setOrderDeliveryInfo,
  setOrderStatus,
  checkActivityLevel,
  updateOrderItems,
  setOrderInfo
} from "state/orderEdition/actions";
import { Form, Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import swal from "sweetalert";
import moment from "moment";

const findTenderCatalogItem = (tenderCatalog, item) => {
  return tenderCatalog?.items?.find((catalogItem) => {
    if (catalogItem.type === "product" && Number(catalogItem.product_id) === Number(item.product_id)) {
      return true;
    }
    if (catalogItem.type === "package" && Number(catalogItem.package_id) === Number(item.package_id)) {
      return true;
    }
    if (catalogItem.type === "group" && Number(catalogItem.group_id) === Number(item.group_id)) {
      return true;
    }
    return false;
  });
};

const resolveTenderCatalogItemPricing = (tenderCatalogItem, personCount, prestationType) => {
  if (!tenderCatalogItem || !personCount || !prestationType) {
    return null;
  }

  let tenderCatalogItemPricing = tenderCatalogItem?.pricings?.find(
    (pricing) =>
      pricing.min_persons <= personCount &&
      pricing.max_persons >= personCount &&
      pricing.price !== null &&
      pricing.prestation_types &&
      pricing.prestation_types.includes(prestationType)
  );

  if (!tenderCatalogItemPricing) {
    const pricingsWithPrice = tenderCatalogItem?.pricings?.filter(
      (pricing) =>
        pricing.price !== null &&
        pricing.prestation_types &&
        pricing.prestation_types.includes(prestationType) &&
        pricing.min_persons <= personCount
    );

    const closestPricing = pricingsWithPrice?.reduce((closest, pricing) => {
      const currentDistance = Math.abs(pricing.max_persons - personCount);
      const closestDistance = Math.abs(closest.max_persons - personCount);
      return currentDistance < closestDistance ? pricing : closest;
    }, pricingsWithPrice[0]);

    if (closestPricing) {
      tenderCatalogItemPricing = closestPricing;
    }
  }

  return tenderCatalogItemPricing || null;
};

const getPricingTierKey = (pricing) => {
  if (!pricing) {
    return null;
  }
  if (pricing.range_id != null) {
    return `range:${pricing.range_id}`;
  }
  return `persons:${pricing.min_persons}-${pricing.max_persons}`;
};

// Petit point d'interrogation avec une bulle d'aide (tooltip).
const HelpTip = ({ id, children }) => (
  <OverlayTrigger placement="top" overlay={<Tooltip id={id}>{children}</Tooltip>}>
    <span
      style={{
        display: "inline-block",
        marginLeft: 6,
        width: 16,
        height: 16,
        lineHeight: "16px",
        textAlign: "center",
        borderRadius: "50%",
        backgroundColor: "#6c757d",
        color: "#fff",
        fontSize: 11,
        fontWeight: 700,
        cursor: "help",
        verticalAlign: "middle",
      }}
    >
      ?
    </span>
  </OverlayTrigger>
);

const HeaderOrderInfoComponent = ({ orderIndex, showStatus, tenderCatalog, products, productPackages, productGroups }) => {
  const dispatch = useDispatch();
  const deliveryInfo = useSelector(
    (state) => state.orderEdition.orders[orderIndex]?.deliveryInfo
  );
  
  const items = useSelector((state) => state.orderEdition.orders[orderIndex].items);

  const order = useSelector((state) => state.orderEdition.orders[orderIndex]);
  const hasTender = useSelector((state) => !!state.orderEdition.tender?.id);
  const prestationType = useSelector(
    (state) => state.orderEdition.orders[orderIndex].prestationType
  );
  const orderStatus = useSelector(
    (state) => state.orderEdition.orders[orderIndex].status
  );

  const previousPersonRef = useRef(deliveryInfo?.person);
  const isEditingPersonRef = useRef(false);

  const [deliveryDate, setDeliveryDate] = useState(deliveryInfo?.deliveryDate || "");
  const [person, setPerson] = useState(deliveryInfo?.person || "");
  const [startAt, setStartAt] = useState(deliveryInfo?.startAt || "");
  const [endAt, setEndAt] = useState(deliveryInfo?.endAt || "");
  const [arrivedAtStart, setArrivedAtStart] = useState(deliveryInfo?.arrivedAtStart || "");
  const [arrivedAtEnd, setArrivedAtEnd] = useState(deliveryInfo?.arrivedAtEnd || "");
  const [recoveryDate, setRecoveryDate] = useState(deliveryInfo?.recoveryDate || "");
  const [recoveryStartAt, setRecoveryStartAt] = useState(deliveryInfo?.recoveryStartAt || "");
  const [recoveryEndAt, setRecoveryEndAt] = useState(deliveryInfo?.recoveryEndAt || "");
  const [blanket, setBlanket] = useState(deliveryInfo?.blanket || false);
  const [withService, setWithService] = useState(deliveryInfo?.service || false);
  const [packagingType, setPackagingType] = useState(order.packagingType || "");

  // Synchroniser l'état local avec le store quand deliveryInfo change
  useEffect(() => {
    setDeliveryDate(deliveryInfo?.deliveryDate || "");
    setPerson(deliveryInfo?.person || "");
    setStartAt(deliveryInfo?.startAt || "");
    setEndAt(deliveryInfo?.endAt || "");
    setArrivedAtStart(deliveryInfo?.arrivedAtStart || "");
    setArrivedAtEnd(deliveryInfo?.arrivedAtEnd || "");
    setRecoveryDate(deliveryInfo?.recoveryDate || "");
    setRecoveryStartAt(deliveryInfo?.recoveryStartAt || "");
    setRecoveryEndAt(deliveryInfo?.recoveryEndAt || "");
    setBlanket(deliveryInfo?.blanket || false);
    setWithService(deliveryInfo?.service || false);
    if (!isEditingPersonRef.current) {
      previousPersonRef.current = deliveryInfo?.person;
    }
  }, [deliveryInfo]);

  useEffect(() => {
    previousPersonRef.current = deliveryInfo?.person;
    isEditingPersonRef.current = false;
    // Réinitialise la référence au changement de prestation, pas à chaque frappe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderIndex]);

  useEffect(() => {
    setPackagingType(order?.packagingType || "");
  }, [order]);

  const computeItemPriceForPersonCount = (item, personCount) => {
    const tenderCatalogItem = findTenderCatalogItem(tenderCatalog, item);
    if (!tenderCatalogItem) {
      return item.price;
    }

    let packageParticipantsNumber = 1;
    let itemInitialPrice = null;

    if (item.type === "productPackage" && productPackages) {
      const productPackage = productPackages.find((g) => g.id === Number(item.package_id));
      if (productPackage) {
        packageParticipantsNumber = productPackage.participants_number || 1;
        itemInitialPrice = productPackage.price;
      }
    }
    if (item.type === "product" && products) {
      const product = products.find((g) => g.id === Number(item.product_id));
      if (product) {
        itemInitialPrice = product.price;
      }
    }
    if (item.type === "productGroup" && productGroups) {
      const productGroup = productGroups.find((g) => g.id === Number(item.group_id));
      if (productGroup) {
        itemInitialPrice = productGroup.price;
      }
    }

    const tenderCatalogItemPricing = resolveTenderCatalogItemPricing(
      tenderCatalogItem,
      personCount,
      prestationType
    );

    if (tenderCatalogItemPricing && tenderCatalogItemPricing.price !== null) {
      return tenderCatalogItemPricing.price / packageParticipantsNumber;
    }
    if (tenderCatalogItem.price !== null && tenderCatalogItem.price !== undefined) {
      return tenderCatalogItem.price / packageParticipantsNumber;
    }
    if (itemInitialPrice !== null) {
      return itemInitialPrice / packageParticipantsNumber;
    }
    return item.price;
  };

  const hasCatalogItemsTierChange = (oldPersonCount, newPersonCount) => {
    return items.some((item) => {
      const tenderCatalogItem = findTenderCatalogItem(tenderCatalog, item);
      if (!tenderCatalogItem) {
        return false;
      }

      const oldPricing = resolveTenderCatalogItemPricing(
        tenderCatalogItem,
        oldPersonCount,
        prestationType
      );
      const newPricing = resolveTenderCatalogItemPricing(
        tenderCatalogItem,
        newPersonCount,
        prestationType
      );

      return getPricingTierKey(oldPricing) !== getPricingTierKey(newPricing);
    });
  };

  const applyItemPricesForPersonCount = (newPersonCount) => {
    const updatedItems = items.map((item) => ({
      ...item,
      price: computeItemPriceForPersonCount(item, newPersonCount),
    }));
    dispatch(updateOrderItems(orderIndex, updatedItems));
  };

  const handleInputChange = (field, value) => {
    if (["packagingType"].includes(field)) {
      dispatch(
        setOrderInfo(orderIndex, {
          ...order,
          packagingType: value && value !== "" ? value : null,
        })
      );
      return;
    }

    const updatedDeliveryInfo = { ...deliveryInfo, [field]: value };
    dispatch(setOrderDeliveryInfo(orderIndex, updatedDeliveryInfo));
  };

  const handlePersonBlur = async () => {
    isEditingPersonRef.current = false;

    const newPersonCount = parseInt(person, 10);
    if (!Number.isFinite(newPersonCount) || newPersonCount <= 0) {
      previousPersonRef.current = person;
      return;
    }

    const oldPersonCount = parseInt(previousPersonRef.current, 10);
    previousPersonRef.current = person;

    if (!Number.isFinite(oldPersonCount) || newPersonCount === oldPersonCount) {
      return;
    }

    if (!hasTender || !tenderCatalog?.items?.length || !prestationType) {
      return;
    }

    if (!hasCatalogItemsTierChange(oldPersonCount, newPersonCount)) {
      return;
    }

    const confirmUpdate = await swal({
      title: "Changement de palier tarifaire",
      text:
        "Le nombre de personnes correspond à un autre palier du catalogue AO. Souhaitez-vous actualiser les prix des produits, forfaits et groupes ?",
      icon: "warning",
      buttons: ["Non, conserver les prix actuels", "Oui, actualiser"],
      dangerMode: false,
    });

    if (confirmUpdate) {
      applyItemPricesForPersonCount(newPersonCount);
    }
  };

  const [status, setStatus] = useState(orderStatus || "");

  useEffect(() => {
    if (status !== orderStatus) {
      dispatch(setOrderStatus(orderIndex, status));
    }
  }, [dispatch, orderIndex, orderStatus, status]);

  let statusOptions = [
    {
      option: "101",
      traduction: "En attente",
    },
    {
      option: "102",
      traduction: "Validé",
    },
    {
      option: "103",
      traduction: "A vérifier avant facturation",
    },
    {
      option: "401",
      traduction: "Refusé par le client",
    },
  ];

  const handleDeliveryDateChange = async (e) => {
    const formattedDate = moment(new Date(e.target.value)).format("YYYY-MM-DD");

    // Niveau d'activité
    let confirmActivity = true;
    confirmActivity = await dispatch(checkActivityLevel(prestationType, formattedDate)).then(
      (response) => {
        let swalMsg = null;
        if (response?.isClosed === true) {
          swalMsg = "L'activité est bloqué pour cette date, êtes-vous sûr de vouloir continuer ?";
        }
        if (response?.activityLevelTxt) {
          if ('medium' === response.activityLevelTxt) {
            swalMsg = "L'activité est forte pour cette date, êtes-vous sûr de vouloir continuer ?";
          }
          else if ('high' === response.activityLevelTxt) {
            swalMsg = "L'activité est bloqué pour cette date, êtes-vous sûr de vouloir continuer ?";
          }
          else if ('low' === response.activityLevelTxt) {
            swalMsg = "L'activité est faible pour cette date, êtes-vous sûr de vouloir continuer ?";
          }
        }

        if (swalMsg && 'low' !== response.activityLevelTxt) {
          return swal({
            title: "Attention ",
            text: swalMsg,
            icon: "warning",
            buttons: true,
            dangerMode: true,
          }).then((confirm) => {
            return confirm;
          });
        }
        return true;
      }
    );
    if (!confirmActivity) {
      return;
    }

    setDeliveryDate(formattedDate);
    handleInputChange("deliveryDate", formattedDate);

    // La récupération ne peut pas être antérieure à la livraison : si la nouvelle date
    // de livraison passe après une récup déjà saisie, on efface la récup devenue invalide.
    if (recoveryDate && recoveryDate < formattedDate) {
      setRecoveryDate("");
      handleInputChange("recoveryDate", "");
      swal({
        title: "Date de récupération réinitialisée",
        text: "La date de récupération était antérieure à la nouvelle date de livraison : elle a été vidée. Merci de la re-saisir si besoin.",
        icon: "info",
      });
    }
  }

  return (
    <>
      <Row>
        {showStatus && (
          <Col>
            <Form.Group controlId="statusSelect">
              <Form.Label>Statut*</Form.Label>
              <Form.Control
                as="select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">Sélectionnez un statut</option>
                {statusOptions.map((sta, index) => (
                  <option key={index} value={sta.option}>
                    {sta.traduction}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
        )}
        <Col>
          <Form.Group controlId="deliveryDate">
            <Form.Label>Date de livraison*</Form.Label>
            <Form.Control
              type="date"
              value={deliveryDate}
              onChange={(e) => {
                handleDeliveryDateChange(e);
              }}
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="person">
            <Form.Label>Nombre de personnes*</Form.Label>
            <Form.Control
              type="number"
              value={person}
              onChange={(e) => {
                isEditingPersonRef.current = true;
                setPerson(e.target.value);
                handleInputChange("person", e.target.value);
              }}
              onBlur={handlePersonBlur}
              required
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="packagingType">
            <Form.Label>Type de packaging</Form.Label>
            <Form.Control
              as="select"
              value={packagingType}
              onChange={(e) => {
                setPackagingType(e.target.value);
                handleInputChange("packagingType", e.target.value);
              }}
            >
              <option value="">Normal</option>
              <option value="zero_waste">Zéro déchet</option>
              <option value="picnic">Pique-nique</option>
              <option value="delivery_cardboard">Carton livraison</option>
            </Form.Control>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Group controlId="arrivedAtStart">
            <Form.Label>Début livraison</Form.Label>
            <Form.Control
              type="time"
              value={arrivedAtStart}
              onChange={(e) => {
                setArrivedAtStart(e.target.value);
                handleInputChange("arrivedAtStart", e.target.value);
              }}
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="arrivedAtEnd">
            <Form.Label>Fin livraison</Form.Label>
            <Form.Control
              type="time"
              value={arrivedAtEnd}
              onChange={(e) => {
                setArrivedAtEnd(e.target.value);
                handleInputChange("arrivedAtEnd", e.target.value);
              }}
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="startAt">
            <Form.Label>Début presta</Form.Label>
            <Form.Control
              type="time"
              value={startAt}
              onChange={(e) => {
                setStartAt(e.target.value);
                handleInputChange("startAt", e.target.value);
              }}
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="endAt">
            <Form.Label>Fin presta</Form.Label>
            <Form.Control
              type="time"
              value={endAt}
              onChange={(e) => {
                setEndAt(e.target.value);
                handleInputChange("endAt", e.target.value);
              }}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={4}>
          <Form.Group controlId="recoveryDate">
            <Form.Label>
              Date de récupération
              <HelpTip id="tip-recovery-date">
                Si une date est renseignée, une <strong>récupération est planifiée</strong> pour
                cette prestation (matériel/vaisselle à récupérer après la livraison).
                Laisser vide = aucune récupération prévue. Par défaut, sur les prestations
                concernées : le <strong>lendemain</strong> de la prestation (reporté au lundi si
                week-end).
              </HelpTip>
            </Form.Label>
            <Form.Control
              type="date"
              value={recoveryDate}
              min={deliveryDate || undefined}
              onChange={(e) => {
                const v = e.target.value;
                if (v && deliveryDate && v < deliveryDate) {
                  swal({
                    title: "Date invalide",
                    text: "La date de récupération ne peut pas être antérieure à la date de livraison.",
                    icon: "warning",
                  });
                  return;
                }
                setRecoveryDate(v);
                handleInputChange("recoveryDate", v);
              }}
            />
            <Form.Text className="text-muted">Renseignée = récupération prévue (≥ date de livraison)</Form.Text>
          </Form.Group>
        </Col>
        <Col xs={6} md={4}>
          <Form.Group controlId="recoveryStartAt">
            <Form.Label>
              Récup. — début
              <HelpTip id="tip-recovery-start">
                Heure de début de la plage pendant laquelle la récupération peut être
                effectuée (par défaut 10:00).
              </HelpTip>
            </Form.Label>
            <Form.Control
              type="time"
              value={recoveryStartAt}
              onChange={(e) => {
                setRecoveryStartAt(e.target.value);
                handleInputChange("recoveryStartAt", e.target.value);
              }}
            />
          </Form.Group>
        </Col>
        <Col xs={6} md={4}>
          <Form.Group controlId="recoveryEndAt">
            <Form.Label>
              Récup. — fin
              <HelpTip id="tip-recovery-end">
                Heure de fin de la plage pendant laquelle la récupération peut être
                effectuée (par défaut 15:00).
              </HelpTip>
            </Form.Label>
            <Form.Control
              type="time"
              value={recoveryEndAt}
              onChange={(e) => {
                setRecoveryEndAt(e.target.value);
                handleInputChange("recoveryEndAt", e.target.value);
              }}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Group controlId="blanket">
            <Form.Check
              type="checkbox"
              label="Nappe"
              checked={blanket}
              onChange={(e) => {
                setBlanket(e.target.checked);
                handleInputChange("blanket", e.target.checked);
              }}
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="withService">
            <Form.Check
              type="checkbox"
              label="Avec service"
              checked={withService}
              onChange={(e) => {
                setWithService(e.target.checked);
                handleInputChange("service", e.target.checked);
              }}
            />
          </Form.Group>
        </Col>
      </Row>
    </>
  );
};

export default HeaderOrderInfoComponent;